import React, { useContext } from "react";
import { usePageIntro } from "common/NewLook";
import Features from "features/settings/CancelSubscription/Features.tsx";
import styled, { css, useTheme } from "styled-components";
import CancelSubscriptionProvider from "features/settings/CancelSubscription/CancelSubscriptionProvider.tsx";
import { CancelSubscriptionContext } from "@shared/features/settings/subscription/CancelSubscription.context.ts";
import AreYouSure from "features/settings/CancelSubscription/AreYouSure.tsx";
import Success from "features/settings/CancelSubscription/Success.tsx";
import CancellationReasons from "features/settings/CancelSubscription/CancellationReasons.tsx";
import { PAGE_SPINNER_MARGIN_FROM_TOP } from "common/constants.ts";
import LoseDiscount from "features/settings/CancelSubscription/LoseDiscount.tsx";
import Footer from "features/settings/CancelSubscription/Footer.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import {
  STICKY_FOOTER_LAYOUT_MIN_WIDTH,
  VERTICAL_LAYOUT_MIN_WIDTH,
} from "features/settings/CancelSubscription/constants.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { useDisablePageScroll } from "utils/hooks/misc.tsx";
import { CancellationStep } from "@shared/features/settings/subscription/types.ts";

const CancelSubscription = () => {
  return (
    <CancelSubscriptionProvider>
      <InnerCancelSubscription />
    </CancelSubscriptionProvider>
  );
};

export default CancelSubscription;

const InnerCancelSubscription = () => {
  const theme = useTheme();
  // Focused flow with its own title and back button.
  usePageIntro({ hidden: theme.isNewLook }, [theme.isNewLook]);
  const { step, isLoading, getBackButton } = useContext(
    CancelSubscriptionContext
  )!;

  const isStickyFooter = useMedia(
    [`(max-width: ${STICKY_FOOTER_LAYOUT_MIN_WIDTH}px)`],
    [true],
    false
  );

  useDisablePageScroll();

  const getContent = () => {
    if (step === CancellationStep.MISSING_FEATURES) {
      return <Features />;
    } else if (step === CancellationStep.LOSE_DISCOUNT) {
      return <LoseDiscount />;
    } else if (step === CancellationStep.REASON) {
      return <CancellationReasons />;
    } else if (step === CancellationStep.ARE_YOU_SURE) {
      return <AreYouSure />;
    } else if (step === CancellationStep.SUCCESS) {
      return <Success />;
    }
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      hideContentWhileLoading={true}
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      spinnerSize="large"
      backgroundColor={theme.colors.layoutBgColor}
      wrapStyle={{ height: "100%", position: "relative" }}
    >
      <Wrapper>
        <InnerWrapper>
          <ContentWrapper $step={step}>
            {getBackButton()}
            {getContent()}
            {!isStickyFooter && <Footer />}
          </ContentWrapper>
        </InnerWrapper>
        {isStickyFooter && <Footer />}
      </Wrapper>
    </LoadingSpinnerOverlay>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  @media (min-width: ${STICKY_FOOTER_LAYOUT_MIN_WIDTH}px) {
    overflow-y: auto;
  }
`;
const InnerWrapper = styled.div`
  @media (max-width: ${STICKY_FOOTER_LAYOUT_MIN_WIDTH}px) {
    flex: 1;
    overflow-y: auto;
  }
  @media (min-width: ${STICKY_FOOTER_LAYOUT_MIN_WIDTH + 1}px) {
    height: 100%;
  }
`;
const ContentWrapper = styled.div<{
  $step: CancellationStep;
}>`
  min-height: 100%;
  padding: 16px;
  @media (min-width: ${STICKY_FOOTER_LAYOUT_MIN_WIDTH}px) {
    padding: 16px 40px;
  }
  margin: 0 auto;
  max-width: 1125px;
  @media (max-width: ${VERTICAL_LAYOUT_MIN_WIDTH}px) {
    max-width: 500px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      max-width: 760px;
      min-height: 0;
      padding: 16px 28px 32px;
      > button:first-child {
        align-self: flex-start;
      }
      @media (max-width: ${VERTICAL_LAYOUT_MIN_WIDTH}px) {
        max-width: 760px;
      }
      @media (max-width: ${STICKY_FOOTER_LAYOUT_MIN_WIDTH}px) {
        padding: 12px 16px 20px;
      }
    `}
`;
